import {
	SET_FORM,
	HAS_ERROR,
	SET_HAS_ERROR,
	DELETE_PDF_FORM,
	SET_DELETE_PDF_FORM,
	TOAST_MESSAGES_PUSH
} from './mutation-types';
import { ApiService as api } from '@/services/axiosHelper';
import { DownloadService as downloadApi } from '@/services/axiosHelper';
import { UploadService as uploadApi } from '@/services/axiosHelper';
import _ from 'lodash';

const parseImpactPayload = fields => {
	fields.forEach(item => {
		const { truncate, characterNumber, impactTruncate } = item;
		let newImpactTruncate =
			truncate && characterNumber
				? `${truncate}${characterNumber}`
				: null;

		if (newImpactTruncate !== impactTruncate) {
			item.impactTruncate = newImpactTruncate;
		}
		delete item['truncate'];
		delete item['characterNumber'];
	});
	return fields;
};

export default {
	strict: process.env.NODE_ENV !== 'production',
	name: 'form',
	namespaced: true,
	state: {
		form: {},
		[HAS_ERROR]: false,
		[DELETE_PDF_FORM]: false
	},
	mutations: {
		[SET_FORM](state, form) {
			state.form = form;
		},
		[SET_HAS_ERROR](state, value) {
			state[HAS_ERROR] = value;
		},
		[SET_DELETE_PDF_FORM](state, value) {
			state[DELETE_PDF_FORM] = value;
		}
	},
	actions: {
		[SET_FORM]({ commit }, form) {
			commit(SET_FORM, form);
		},
		async getForm({ dispatch }, id) {
			let result = await api.get(`/forms/${id}`);
			return dispatch('parseImpactResponse', result);
		},
		async updateForm({ dispatch }, form) {
			if (!_.isEmpty(form.impactInputFields)) {
				let newFields = parseImpactPayload(form.impactInputFields);
				form.impactInputFields = newFields;
			}
			let result = await api.put(`/forms/${form.id}`, { ...form });
			return dispatch('parseImpactResponse', result);
		},
		async saveForm({ dispatch }, form) {
			if (!_.isEmpty(form.impactInputFields)) {
				let newFields = parseImpactPayload(form.impactInputFields);
				form.impactInputFields = newFields;
			}
			const result = await api.post(`/forms`, { ...form });
			return dispatch('parseImpactResponse', result);
		},
		async undoFormChanges({ dispatch }, id) {
			const result = await api.post(`/forms/undochanges/${id}`);
			return dispatch('parseImpactResponse', result);
		},
		parseImpactResponse({ commit }, form) {
			if (!_.isEmpty(form.impactInputFields)) {
				const isImpactTruncateValid = /([l,r,L,R]+[1-9]{1}$|[l,r,L,R]+[1-9]{1}[0-9]{1}$|[l,r,L,R]+50$)/;
				form.impactInputFields.forEach(item => {
					if (
						item.impactTruncate &&
						item.impactTruncate.match(isImpactTruncateValid)
					) {
						item.truncate = item.impactTruncate
							.charAt(0)
							.toUpperCase();
						item.characterNumber = parseInt(
							item.impactTruncate.substring(
								1,
								item.impactTruncate.length
							)
						);
					} else {
						item.truncate = null;
						item.characterNumber = 0;
						item.impactTruncate = null;
					}
				});
			}
			commit(SET_FORM, form);
			return form;
		},
		[SET_HAS_ERROR]({ commit }, value) {
			commit(SET_HAS_ERROR, value);
		},
		async deactivateActivateForm({ dispatch, state }, { id, action }) {
			let isSuccessful = true;
			let verbiage = 'successfully';
			let isActive = state.form.active;
			await api
				.post(`/forms/${action}/${id}`)
				.then(() => {
					state.form.active = !isActive;
					state.form.minutesAgo = 0;
				})
				.catch(() => {
					isSuccessful = false;
					verbiage = 'unsuccessfully';
				})
				.finally(() => {
					dispatch(
						TOAST_MESSAGES_PUSH,
						{
							statusCode: isSuccessful ? 200 : 400,
							message: `Form ${verbiage} ${action}d.`
						},
						{
							root: true
						}
					);
				});
			return isSuccessful;
		},
		async getPdfFile({ dispatch }, pdfData) {
			let result = [];
			await downloadApi
				.download(`/forms/pdfs?PdfData=${pdfData}`)
				.then(response => {
					result = response;
				})
				.catch(() => {
					dispatch(
						TOAST_MESSAGES_PUSH,
						{
							statusCode: 400,
							message: 'File not found.'
						},
						{
							root: true
						}
					);
				});
			return result;
		},
		async uploadFile({ dispatch }, formData) {
			let result = {};
			let isSuccessful = true;
			let verbiage = 'successfully';

			await uploadApi
				.postFile('/forms/pdfs/', formData)
				.then(response => {
					result = { ...response };
				})
				.catch(() => {
					isSuccessful = false;
					verbiage = 'unsuccessfully';
				})
				.finally(() => {
					dispatch(
						TOAST_MESSAGES_PUSH,
						{
							statusCode: isSuccessful ? 200 : 400,
							message: `File ${verbiage} replaced.`
						},
						{
							root: true
						}
					);
				});
			return result;
		},
		async assignCustomers({ dispatch }, { id, customers }) {
			let isSuccessful = true;
			let verbiage = 'successfully';

			await api
				.post(`/forms/assign/${id}`, {
					customers: customers
				})
				.then()
				.catch(() => {
					isSuccessful = false;
					verbiage = 'unsuccessfully';
				})
				.finally(() => {
					dispatch(
						TOAST_MESSAGES_PUSH,
						{
							statusCode: isSuccessful ? 200 : 400,
							message: `Clients ${verbiage} updated.`
						},
						{
							root: true
						}
					);
				});
			return isSuccessful;
		},
		async publishFormData({ dispatch, commit }, id) {
			let isSuccessful = true;
			let result = {};
			let verbiage = 'successfully';

			await api
				.post(`/forms/publish/${id}`)
				.then(resp => {
					result = _.cloneDeep(resp);
					commit(SET_FORM, result);
				})
				.catch(() => {
					isSuccessful = false;
					verbiage = 'unsuccessfully';
				})
				.finally(() => {
					dispatch(
						TOAST_MESSAGES_PUSH,
						{
							statusCode: isSuccessful ? 200 : 400,
							message: `Form ${verbiage} published.`
						},
						{
							root: true
						}
					);
				});
			return result;
		}
	},
	getters: {
		form: state => state.form,
		[HAS_ERROR]: state => state[HAS_ERROR]
	}
};
